import { useState, useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { ANNOUNCEMENT_TYPES, ANNOUNCEMENT } from '../../graphql/announcement-queries'
import {
  CREATE_ANNOUNCEMENT,
  PUBLISH_ANNOUNCEMENT,
  UPDATE_ANNOUNCEMENT,
} from '../../graphql/announcement-mutations'
import LoadingSpinner from '../../components/LoadingSpinner'
import AudiencePicker from '../../components/announcements/AudiencePicker'
import type { AnnouncementType, AudienceRule } from '../../types/announcement'

const AnnouncementFormPage = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id
  const announcementId = id ? parseInt(id, 10) : null

  const [typeId, setTypeId] = useState<number | ''>('')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [audience, setAudience] = useState<AudienceRule>({ all: true })

  // Type picker (active types only)
  const { data: typesData } = useQuery(ANNOUNCEMENT_TYPES, {
    variables: { activeOnly: true },
  })
  const types: AnnouncementType[] = typesData?.announcementTypes ?? []
  const selectedType = types.find((t) => t.id === typeId)

  // Edit mode — load the existing announcement (URL-reachable, can't rely on cache)
  const { data: editData, loading: loadingEdit } = useQuery(ANNOUNCEMENT, {
    variables: { id: announcementId },
    skip: !isEdit,
    fetchPolicy: 'cache-and-network',
  })

  useEffect(() => {
    const a = editData?.announcement
    if (a) {
      setTypeId(a.type.id)
      setTitle(a.title)
      setBody(a.body)
    }
  }, [editData])

  const [createAnnouncement] = useMutation(CREATE_ANNOUNCEMENT)
  const [publishAnnouncement] = useMutation(PUBLISH_ANNOUNCEMENT)
  const [updateAnnouncement] = useMutation(UPDATE_ANNOUNCEMENT)
  const [submitting, setSubmitting] = useState(false)

  const validate = (): boolean => {
    if (!typeId) { toast.error('Pick a type'); return false }
    if (!title.trim()) { toast.error('Title is required'); return false }
    if (!body.trim()) { toast.error('Body is required'); return false }
    return true
  }

  // Create-only: a role/user-targeted rule must name at least one recipient.
  // (The server also rejects a zero-recipient publish, but catch it earlier.)
  const validateAudience = (): boolean => {
    if (audience.all) return true
    const count = (audience.roles?.length ?? 0) + (audience.users?.length ?? 0)
    if (count === 0) {
      toast.error('Pick at least one role or person, or choose Everyone')
      return false
    }
    return true
  }

  const handleCreateAndPublish = async () => {
    if (!validate() || !validateAudience()) return
    setSubmitting(true)
    try {
      // Create produces a draft; publishing fans out receipts to the resolved audience.
      const created = await createAnnouncement({
        variables: {
          input: { type_id: typeId, title: title.trim(), body, audience_rule: audience },
        },
      })
      const newId = created.data?.createAnnouncement?.id
      if (!newId) throw new Error('Create did not return an id')
      await publishAnnouncement({ variables: { id: newId } })
      toast.success('Announcement published')
      navigate('/admin/announcements')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to publish')
    } finally {
      setSubmitting(false)
    }
  }

  const handleSave = async () => {
    if (!validate() || announcementId == null) return
    if (!window.confirm('This updates the live announcement everyone sees. Existing acknowledgements are kept. Continue?')) {
      return
    }
    setSubmitting(true)
    try {
      await updateAnnouncement({
        variables: {
          id: announcementId,
          input: { type_id: typeId, title: title.trim(), body },
        },
      })
      toast.success('Announcement updated')
      navigate('/admin/announcements')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update')
    } finally {
      setSubmitting(false)
    }
  }

  if (isEdit && loadingEdit && !editData) {
    return <div className="flex justify-center py-12"><LoadingSpinner /></div>
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/admin/announcements" className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {isEdit ? 'Edit Announcement' : 'New Announcement'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {isEdit
              ? 'Edits apply in place to the live announcement.'
              : 'Publishes immediately to everyone.'}
          </p>
        </div>
      </div>

      <form onSubmit={(e) => e.preventDefault()} className="card p-6 space-y-5">
        {/* Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type <span className="text-red-500">*</span>
          </label>
          <select
            value={typeId}
            onChange={(e) => setTypeId(e.target.value ? parseInt(e.target.value, 10) : '')}
            className="input w-full"
          >
            <option value="">Select a type…</option>
            {types.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
          {selectedType?.requires_ack && (
            <p className="mt-1 text-xs text-amber-700">⚠ This type requires acknowledgement from each recipient.</p>
          )}
        </div>

        {/* Audience — chosen at authoring, resolved server-side at publish.
            Hidden when editing: recipients are fixed at publish and edits don't re-fan-out. */}
        {!isEdit && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Audience</label>
            <AudiencePicker value={audience} onChange={setAudience} />
          </div>
        )}

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. New closing checklist"
            className="input w-full"
          />
        </div>

        {/* Body */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Body <span className="text-red-500">*</span>
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            placeholder="Plain text. What changed and what staff should do now."
            className="input w-full"
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <Link to="/admin/announcements" className="btn-secondary">Cancel</Link>
          {isEdit ? (
            <button type="button" onClick={handleSave} disabled={submitting} className="btn-primary">
              {submitting ? 'Saving…' : 'Save'}
            </button>
          ) : (
            <button type="button" onClick={handleCreateAndPublish} disabled={submitting} className="btn-primary">
              {submitting ? 'Publishing…' : 'Publish'}
            </button>
          )}
        </div>
      </form>
    </div>
  )
}

export default AnnouncementFormPage
